// The page of a news, connected: /news/:origin/:id asks the item's detail
// (GET /items/{origin}/{item_id}, the same key the card opened in place
// uses, so a news opened from its card asks nothing again), the catalogue
// for the chips' links, and hands NewsReader what toItemPage makes of them.
// The bookmark goes through useBookmarks, as the lists' do. A Coro document
// has its types in Italian and the section «Parlamento» (toParliamentFacts)
// after the iter, where the source's updates are said instead of the
// metadata's versions; its index (GET /items/{origin}/{item_id}/sections)
// is asked once the detail says it has a text and, when it has more than
// one entry, the page takes the frame of the lists: the index in the panel,
// the text cut at the entries on the right, «vai al punto» between them;
// `?voce=<ordinal>` opens the page at that entry (from a sitting's page).
// The official page is offered when the detail says `has_html`.
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router";

import { isApiError } from "../api/client";
import { PanelStatus } from "../components/PanelStatus";
import { NewsActions } from "../components/news/NewsActions";
import { NewsCardPlaceholder } from "../components/news/NewsCardPlaceholder";
import { TextLoading } from "../components/news/NewsCard";
import { NewsReader, NewsReaderFrame } from "../components/news/NewsReader";
import { SearchFinder } from "../components/news/SearchFinder";
import { FINDER_PARAM, readFinderParam } from "../lib/finder";
import { backLinkState } from "../lib/navigation";
import { OfficialPage } from "../components/parliament/OfficialPage";
import { ParliamentFacts } from "../components/parliament/ParliamentFacts";
import { ParliamentReader, type ReaderTarget } from "../components/parliament/ParliamentReader";
import { INDEX_TITLE, SectionIndex } from "../components/parliament/SectionIndex";
import { runtimeConfig } from "../config/runtime";
import {
  backLabel,
  backPath,
  parseItemId,
  toItemPage,
  toSourceTree,
  type Origin,
  type ReaderNotice,
} from "../features/news/adapt";
import { useItem, useSources } from "../features/news/api";
import { useBookmarks } from "../features/news/bookmarks";
import {
  anchorOf,
  DEFAULT_INDEX_FILTER,
  documentTypeLabel,
  hasParliamentFacts,
  officialPageUrl,
  parliamentNotices,
  readerPages,
  sectionTree,
  shownOrdinals,
  splitAtSections,
  toParliamentFacts,
  voceOf,
  type IndexFilter,
  type ParliamentNotice,
} from "../features/parliament/adapt";
import { useSections } from "../features/parliament/api";
import { SITE_TITLE } from "../config/site";

export const LOADING = "Caricamento della news…";
export const FAILED = "News non disponibile.";
export const NOT_FOUND = "News non trovata: potrebbe essere stata rimossa dalla fonte.";
const NOT_FOUND_TITLE = "News non trovata";

const NOTICE: Record<ReaderNotice, string> = {
  incomplete: "Testo non completo: apri l’originale",
  missing: "Testo non disponibile: apri l’originale",
  "manual-read": "Allegato da leggere a mano (OCR insufficiente)",
};
const PARLIAMENT_NOTICE: Record<ParliamentNotice, string> = {
  "pdf-bulletin": "Bollettino solo in PDF",
  "no-text": "Testo non disponibile: apri il PDF",
  "index-later": "Indice disponibile a fine seduta",
};
export const INDEX_FAILED = "Indice non disponibile.";

/** The page's title in the tab, put back as it was when the page is left. */
function useDocumentTitle(title: string | null) {
  useEffect(() => {
    if (title === null) return;
    const previous = document.title;
    document.title = `${title} · ${SITE_TITLE}`;
    return () => {
      document.title = previous;
    };
  }, [title]);
}

export function NewsItemPage() {
  const { origin = "", id = "" } = useParams();
  // the same check the lists' ids go through: an unknown origin or an empty id is a 404 without a call
  const ref = parseItemId(`${origin}/${id}`);
  if (!ref) return <NotFound />;
  return <ConnectedItemPage key={`${ref.origin}/${ref.id}`} origin={ref.origin} id={ref.id} />;
}

function NotFound() {
  const location = useLocation();
  useDocumentTitle(NOT_FOUND_TITLE);
  // the focus lands on the message, as it lands on the title of a news that is there
  const message = useRef<HTMLDivElement>(null);
  useEffect(() => message.current?.focus(), []);
  const backTo = backPath(location.state);
  return (
    <NewsReaderFrame
      backTo={backTo}
      backState={backLinkState(location.state)}
      backLabel={backLabel(backTo)}
      label={NOT_FOUND_TITLE}
    >
      <div ref={message} tabIndex={-1} className="outline-none">
        <PanelStatus role="alert" indent={false}>
          {NOT_FOUND}
        </PanelStatus>
      </div>
    </NewsReaderFrame>
  );
}

function ConnectedItemPage({ origin, id }: { origin: Origin; id: string }) {
  const location = useLocation();
  const query = useItem(origin, id);
  const catalogue = useSources();
  const bookmarks = useBookmarks();
  const tree = useMemo(() => (catalogue.data ? toSourceTree(catalogue.data) : null), [catalogue.data]);
  const search = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const { apiBase } = runtimeConfig();
  const parliament = origin === "coro";
  // the index is asked only for a document with a text: without one there is nothing to cut
  const wantsIndex = parliament && query.data?.has_text === true;
  const sections = useSections(origin, id, wantsIndex);
  const [filter, setFilter] = useState<IndexFilter>(DEFAULT_INDEX_FILTER);
  // the entry «vai al punto» went to; it goes when the address changes (back, forward), where `?voce=` rules again
  const [chosen, setChosen] = useState<ReaderTarget | null>(null);
  const [chosenAt, setChosenAt] = useState(location.search);
  if (chosenAt !== location.search) {
    setChosenAt(location.search);
    setChosen(null);
  }
  const [officialOpen, setOfficialOpen] = useState(false);
  const page = useMemo(
    () =>
      query.data
        ? toItemPage(
            query.data,
            tree,
            search,
            apiBase,
            parliament ? { typeLabel: documentTypeLabel, revisions: false } : {},
          )
        : null,
    [query.data, tree, search, apiBase, parliament],
  );
  const facts = useMemo(
    () => (parliament && query.data ? toParliamentFacts(query.data) : null),
    [parliament, query.data],
  );
  // the reader with the index: a text and more than one entry (the first is the document itself)
  const text = query.data?.has_text ? (query.data.text ?? null) : null;
  const index = parliament && text !== null && sections.data && sections.data.length > 1 ? sections.data : null;
  const pages = useMemo(
    () => (index && text !== null ? readerPages(splitAtSections(text, index), index) : []),
    [index, text],
  );
  // `?voce=<ordinal>` (an entry of a sitting's page) is the reader's target: the page opens at that point, and
  // follows the address; a document that is not the Parliament's, or an entry the text does not have, has none
  // (the focus goes to the title)
  const voce = parliament ? voceOf(search) : null;
  const fromAddress = useMemo<ReaderTarget | null>(
    () => (voce !== null && index?.some((s) => s.ordinal === voce) ? { ordinal: voce, nonce: 1 } : null),
    [voce, index],
  );
  const target = chosen ?? fromAddress;
  // an entry the pages do not hold (an index past the text in the cache) is not one to go to
  const entries = useMemo(
    () => (index ? sectionTree(index, filter, shownOrdinals(pages)) : []),
    [index, filter, pages],
  );
  const notFound = query.isError && isApiError(query.error) && query.error.status === 404;
  useDocumentTitle(notFound ? NOT_FOUND_TITLE : (page?.news.title ?? null));
  const backTo = backPath(location.state);

  if (notFound) return <NotFound />;
  // a failed call being retried («Riprova») is loading again, not still failed
  if (query.isError && !query.isFetching && !page) {
    return (
      <NewsReaderFrame
        backTo={backTo}
        backState={backLinkState(location.state)}
        backLabel={backLabel(backTo)}
        label={FAILED}
      >
        <PanelStatus role="alert" indent={false} onRetry={() => void query.refetch()}>
          {FAILED}
        </PanelStatus>
      </NewsReaderFrame>
    );
  }
  // a Coro document waits for its index too, so the page opens once in its final frame and nothing jumps
  if (!page || (wantsIndex && sections.isPending)) {
    return (
      <NewsReaderFrame
        backTo={backTo}
        backState={backLinkState(location.state)}
        backLabel={backLabel(backTo)}
        label={LOADING}
      >
        <NewsCardPlaceholder />
        <TextLoading label={LOADING} />
      </NewsReaderFrame>
    );
  }

  let notices = page.notices.map((n) => NOTICE[n]);
  if (parliament && query.data) {
    const own = parliamentNotices(query.data, sections.data ?? null);
    if (own.replacesMissing) notices = notices.filter((n) => n !== NOTICE.missing);
    notices = [...notices, ...own.notices.map((n) => PARLIAMENT_NOTICE[n])];
  }
  const indexFailed = wantsIndex && sections.isError && !sections.isFetching;
  // the official page: offered when the detail says the document has one (`has_html`), at the anchor of the
  // entry «vai al punto» last went to (the Camera's entries carry one; the Senato's open at the top)
  const official =
    parliament && query.data?.has_html ? (
      <OfficialPage
        src={officialPageUrl(apiBase, query.data)}
        anchor={anchorOf(sections.data ?? [], target?.ordinal ?? null)}
        open={officialOpen}
        onToggle={() => setOfficialOpen((open) => !open)}
      />
    ) : null;
  // opened from the results of a search: the bar of its words over the text (decision 27)
  const finderTerms = readFinderParam(search.get(FINDER_PARAM));
  const finder = finderTerms.length > 0 && text ? <SearchFinder terms={finderTerms} text={text} /> : null;
  const extra =
    (facts && hasParliamentFacts(facts)) || indexFailed || official || finder ? (
      <>
        {finder}
        {facts && hasParliamentFacts(facts) && <ParliamentFacts {...facts} />}
        {indexFailed && (
          <PanelStatus role="alert" indent={false} onRetry={() => void sections.refetch()}>
            {INDEX_FAILED}
          </PanelStatus>
        )}
        {official}
      </>
    ) : undefined;

  // The «Apri» of a file is a plain link to the api's download route: no X-Dev-User goes with it,
  // so in development Loggione serves it as its default user (mrossi), whoever the page's user is.
  return (
    <NewsReader
      news={page.news}
      backTo={backTo}
      backState={backLinkState(location.state)}
      backLabel={backLabel(backTo)}
      onToggleSaved={() => bookmarks.toggle(page.news)}
      saveFailure={bookmarks.failure}
      metadata={page.metadata}
      notices={notices}
      actions={<NewsActions url={page.news.url} />}
      files={page.files}
      iter={page.iter}
      chunks={page.chunks}
      totalChars={page.totalChars}
      extra={extra}
      focusTitle={target === null}
      reader={
        index ? (
          <ParliamentReader pages={pages} totalChars={pages.reduce((sum, p) => sum + p.chars, 0)} target={target} />
        ) : undefined
      }
      sidebar={
        index
          ? {
              title: INDEX_TITLE,
              render: (collapse) => (
                <SectionIndex
                  entries={entries}
                  filter={filter}
                  onFilterChange={setFilter}
                  current={target?.ordinal ?? null}
                  onGo={(ordinal) => setChosen({ ordinal, nonce: (target?.nonce ?? 0) + 1 })}
                  onCollapse={collapse}
                />
              ),
            }
          : undefined
      }
    />
  );
}
